import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSAPDataStore } from "../store/SAPDataStore";
import Tabs from "../components/Tabs";
import AllTab from "../components/home/AllTab";
import RecentlyViewedTab from "../components/home/RecentlyViewedTab";
import { useThemeClasses } from "../providers/ThemeProvider";

// Simple cx utility for combining class names
const cx = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

const HomeView = () => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();

  // Named rather than positional: the content switch below used to match on
  // `tabs[0]`/`tabs[1]`/`tabs[2]`, so removing a tab silently re-mapped the rest.
  const recentlyViewedTab = t("recentlyViewed") || "Recently Viewed";
  const allTab = t("all") || "All";
  const tabs = [recentlyViewedTab, allTab];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const viewModeParam = searchParams.get("viewMode");
  const showOnlyWithDataParam = searchParams.get("showOnlyWithData");
  const expandedNodesParam = searchParams.get("expandedNodes");

  // Use SAP data store for filter state AND HomeView state
  const {
    showOnlyWithData,
    setShowOnlyWithData,
    setHomeViewState,
    getHomeViewState,
  } = useSAPDataStore();

  // Get current HomeView state from store
  const currentHomeViewState = getHomeViewState();

  // Initialize state from URL params first, then fall back to store state
  const [activeTab, setActiveTab] = useState(() => {
    // Priority: URL param > store state > default
    if (tabParam && tabs.includes(tabParam)) return tabParam;
    return currentHomeViewState.activeTab &&
      tabs.includes(currentHomeViewState.activeTab)
      ? currentHomeViewState.activeTab
      : tabs[0];
  });

  const [viewMode, setViewMode] = useState<"cards" | "tree" | "globe">(() => {
    // Priority: URL param > store state > default
    if (viewModeParam === "cards" || viewModeParam === "globe")
      return viewModeParam;
    if (viewModeParam === "tree") return "tree";
    return currentHomeViewState.viewMode || "tree";
  });

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Priority: URL param > store state > default
    if (expandedNodesParam) {
      try {
        // Decode URL component in case it was encoded
        const decodedParam = decodeURIComponent(expandedNodesParam);
        // Use pipe separator instead of comma to avoid conflicts with commas in node IDs
        const nodes = decodedParam
          .split("|")
          .filter((node) => node.trim() !== "");
        return new Set(nodes);
      } catch {
        return currentHomeViewState.expandedNodes || new Set();
      }
    }
    return currentHomeViewState.expandedNodes || new Set();
  });

  // Initialize filter state from URL on mount
  useEffect(() => {
    if (showOnlyWithDataParam !== null) {
      setShowOnlyWithData(showOnlyWithDataParam === "true");
    }
  }, [showOnlyWithDataParam, setShowOnlyWithData]);

  // Save HomeView state to global store whenever local state changes
  useEffect(() => {
    setHomeViewState({
      activeTab,
      viewMode,
      expandedNodes,
    });
  }, [activeTab, viewMode, expandedNodes, setHomeViewState]);

  // Memoize expandedNodes as array for URL
  const expandedNodesArray = useMemo(
    () => Array.from(expandedNodes),
    [expandedNodes],
  );

  // Update URL when any state changes
  useEffect(() => {
    // Use pipe separator instead of comma to avoid conflicts with commas in node IDs
    const expandedNodesString =
      expandedNodesArray.length > 0 ? expandedNodesArray.join("|") : "";

    // Encode the expanded nodes to handle special characters
    const encodedExpandedNodes = expandedNodesString
      ? encodeURIComponent(expandedNodesString)
      : "";

    const newParams = {
      tab: activeTab,
      viewMode,
      showOnlyWithData: showOnlyWithData ? "true" : "false",
      expandedNodes: encodedExpandedNodes,
    };

    setSearchParams(newParams);
  }, [
    activeTab,
    viewMode,
    showOnlyWithData,
    expandedNodesArray,
    setSearchParams,
  ]);

  const TabContent =
    activeTab === allTab ? (
      <AllTab
        viewMode={viewMode}
        setViewMode={setViewMode}
        expandedNodes={expandedNodes}
        setExpandedNodes={setExpandedNodes}
      />
    ) : (
      <RecentlyViewedTab />
    );

  return (
    <main className="flex-1 px-2 md:px-8 py-8 mt-2">
      <div className="mb-8">
        <h1 className={cx(themeClasses.textPrimary, "text-4xl font-bold mb-4")}>
          {t("home")}
        </h1>
        <p className={cx(themeClasses.textSecondary, "text-lg")}>
          {t("welcome_message")}
        </p>
      </div>

      <div className={cx(themeClasses.bgSecondary, "rounded-xl p-6")}>
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className="mt-6">{TabContent}</div>
      </div>
    </main>
  );
};

export default HomeView;
